/* eslint-disable no-use-before-define */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode, useEffect, useRef, useState } from 'react'
import {
  Button,
  CheckIcon,
  DatePicker,
  DatePickerProps,
  Input,
  InputCurrency,
  InputCurrencyProps,
  InputNumber,
  InputNumberProps,
  InputProps,
  Select,
  SelectProps,
  SelectSearch,
  SelectSearchProps,
  Text,
  Tooltip,
} from '../../../'

import {
  EditableColumnProps,
  EditableColumnTypeType,
} from './editable-column.type'

import { useTranslation } from 'react-i18next'
import styles from './editable-column.module.css'

export const EditableColumn = ({
  onSubmit,
  type,
  render,
  inputProps,
  value,
  tooltipText,
  hasTooltip = true,
  isEditing,
  onEditingChange,
}: EditableColumnProps) => {
  const { t } = useTranslation('componentsEditableColumn')

  const ref = useRef<HTMLDivElement>(null)

  const [internalEditing, setInternalEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [internalValue, setInternalValue] = useState<any>(value || '')

  const editing = isEditing !== undefined ? !!isEditing : internalEditing

  const updateEditing = (newEditing: boolean) => {
    if (isEditing !== undefined) {
      onEditingChange?.(newEditing)
    } else {
      setInternalEditing(newEditing)
      onEditingChange?.(newEditing)
    }
  }

  const handleCancel = () => {
    setInternalValue(value || '')
    updateEditing(false)
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    try {
      await onSubmit(internalValue)
      updateEditing(false)
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }

  const component: Record<EditableColumnTypeType, ReactNode> = {
    text: (
      <Input
        {...(inputProps as InputProps)}
        value={internalValue as string}
        onChange={(newValue) => setInternalValue(newValue)}
        className={styles.component}
      />
    ),
    number: (
      <InputNumber
        showIcons={false}
        {...(inputProps as InputNumberProps)}
        value={internalValue as string}
        onChange={(newValue) => setInternalValue(newValue)}
        className={styles.component}
      />
    ),
    currency: (
      <InputCurrency
        {...(inputProps as InputCurrencyProps)}
        value={internalValue as string}
        onChange={(newValue) => setInternalValue(newValue)}
        className={styles.component}
      />
    ),
    select: (
      <Select
        {...(inputProps as SelectProps)}
        value={internalValue as string}
        onChange={(newValue) => setInternalValue(newValue)}
        className={styles.component}
      />
    ),
    'select-search': (
      <SelectSearch
        {...(inputProps as SelectSearchProps<any>)}
        onSelectItem={(item) => setInternalValue(item)}
        selectedItem={internalValue}
      />
    ),
    'date-picker': (
      <DatePicker
        {...(inputProps as DatePickerProps)}
        value={internalValue as string}
        onChange={(newValue) => setInternalValue(newValue)}
        className={styles.component}
      />
    ),
  }

  useEffect(() => {
    if (!editing) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        event.stopPropagation()
        handleSubmit()
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        handleCancel()
      }
    }

    const container = ref.current
    if (container) {
      container.addEventListener('keydown', handleKeyDown, true)
    }

    return () => {
      if (container) {
        container.removeEventListener('keydown', handleKeyDown, true)
      }
    }
  }, [editing, handleSubmit, handleCancel])

  useEffect(() => {
    if (editing) {
      const timeoutId = setTimeout(() => {
        if (ref.current) {
          const inputElement = ref.current.querySelector(
            'input, select, textarea',
          ) as HTMLInputElement
          if (inputElement) {
            inputElement.focus()
            if (type === 'text' && inputElement.select) {
              inputElement.select()
            }
          }
        }
      }, 100)

      return () => clearTimeout(timeoutId)
    }
  }, [editing, type])

  useEffect(() => {
    if (!editing) return

    const handleClickOut = (event: MouseEvent) => {
      const target = event.target as Node
      if (ref.current && !ref.current.contains(target)) {
        handleCancel()
      }
    }

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOut)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOut)
    }
  }, [editing, handleCancel])

  useEffect(() => {
    setInternalValue(value || '')
  }, [value])

  return (
    <div
      ref={ref}
      className={styles.core}
      data-editable-column="true"
      onDoubleClick={(e) => {
        if (!editing) {
          updateEditing(true)
        }
        e.stopPropagation()
      }}
      onClick={(e) => {
        if (editing) {
          e.stopPropagation()
        }
      }}
      onMouseDown={(e) => {
        if (editing) {
          e.stopPropagation()
        }
      }}
      tabIndex={editing ? 0 : -1}
    >
      {!editing && (
        <>
          {hasTooltip ? (
            <Tooltip
              text={tooltipText || t('tooltipText')}
              orientation="top"
              widthText="auto"
              className={styles.tooltip}
            >
              {typeof render === 'function'
                ? render({ value, setEditing: updateEditing })
                : render || <Text>{value || '-'}</Text>}
            </Tooltip>
          ) : (
            <div className={styles.tooltip}>
              {typeof render === 'function'
                ? render({ value, setEditing: updateEditing })
                : render || <Text>{value || '-'}</Text>}
            </div>
          )}
        </>
      )}
      {editing && (
        <div
          className={styles.editing}
          onClick={(e) => {
            e.stopPropagation()
          }}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
          onDoubleClick={(e) => {
            e.stopPropagation()
          }}
        >
          {component[type]}
          <div className={styles.actions}>
            <Button
              variant="primary"
              model="round"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                handleSubmit()
              }}
              className={styles['action-button']}
              isActive={!isLoading}
              disabled={isLoading}
            >
              <CheckIcon className={styles.check} />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
